/**
 * @fileoverview Presentational card component for a single row in the
 * Violations panel inventory list.
 * @module template-builder
 *
 * Responsibilities:
 * - Render field breadcrumb, Pass/Fail chip, link-status pill, and
 *   row-level actions (link / change / unlink / fix / go-to-field).
 *
 * Dependencies:
 * - ../../lib/buildViolationInventory
 *
 * Architecture:
 * - Presentation-only; all actions are emitted via callbacks.
 */
import { AlertTriangle, CheckCircle2, ExternalLink, Eye, Link2, Link2Off, Pencil, Wrench } from 'lucide-react';
import type { ViolationInventoryEntry } from '../../lib/buildViolationInventory';

/**
 * @component ViolationInventoryCard
 *
 * Purpose:
 * - One card in the CanvasViolationsPanel list.
 *
 * Props:
 * - entry: The inventory row.
 * - onLink / onChange / onUnlink / onFix: Action callbacks.
 * - onReveal: Jump to the field in the canvas.
 *
 * Data Source:
 * - local (receives entry from parent).
 *
 * Constraints:
 * - Presentation-only; no side effects.
 */
export interface ViolationInventoryCardProps {
  entry: ViolationInventoryEntry;
  onLink: () => void;
  onChange: () => void;
  onUnlink: () => void;
  onFix: () => void;
  onShow: () => void;
  onReveal: () => void;
}

const FIELD_TYPE_LABELS: Record<string, string> = {
  toggle: 'Toggle', dropdown: 'Dropdown', checkboxGroup: 'Checkbox group', radioGroup: 'Radio group',
  text: 'Text', textarea: 'Text area', number: 'Number', measurement: 'Measurement',
  date: 'Date', dateTime: 'Date/Time', camera: 'Camera', photo: 'Photo', signature: 'Signature',
  location: 'Location', section: 'Section', instructions: 'General Information',
  repeatableList: 'Repeatable', dataBound: 'Data-bound', inspectors: 'Inspectors',
};

export function ViolationInventoryCard({
  entry,
  onLink,
  onChange,
  onUnlink,
  onFix,
  onShow,
  onReveal,
}: ViolationInventoryCardProps) {
  const isLinked = entry.status === 'linked';
  const isStale = entry.status === 'stale';
  const isUnlinked = entry.status === 'unlinked';
  const hasBinding = isLinked || isStale;
  const linkedDescription = entry.linkedViolationTitle || 'Description unavailable';

  return (
    <li className="px-3 py-2 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          {/* Row 1: field label + type + breadcrumb */}
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-medium text-gray-900 truncate">{entry.fieldLabel}</span>
            <span className="text-[10px] text-gray-400 shrink-0">{FIELD_TYPE_LABELS[entry.fieldType] ?? entry.fieldType}</span>
          </div>
          <div className="text-[11px] text-gray-500 truncate">
            {entry.formOrder}. {entry.formName}
            {entry.sectionTitle && <span className="text-gray-400"> · {entry.sectionTitle}</span>}
          </div>

          {/* Row 2: status pill(s) */}
          <div className="mt-1 flex items-center gap-1.5 flex-wrap">
            {entry.hasPassFail && (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-1.5 py-0.5">
                <CheckCircle2 size={10} /> Pass/Fail
              </span>
            )}
            {hasBinding && (
              <span
                title={`${entry.linkedViolationCode}${entry.linkedViolationTitle ? ` — ${entry.linkedViolationTitle}` : ''}`}
                className={`inline-flex items-center gap-0.5 text-[10px] font-medium border rounded-full px-1.5 py-0.5 max-w-full truncate ${
                  isStale
                    ? 'text-red-700 bg-red-50 border-red-200'
                    : 'text-emerald-700 bg-emerald-50 border-emerald-200'
                }`}
              >
                <Link2 size={10} />
                <span className="truncate">{entry.linkedViolationCode}{entry.linkedViolationCode ? ' — ' : ''}{linkedDescription}</span>
              </span>
            )}
            {isUnlinked && (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0.5">
                <Link2Off size={10} /> No violation linked
              </span>
            )}
            {isStale && (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-red-700 bg-red-50 border border-red-200 rounded-full px-1.5 py-0.5">
                <AlertTriangle size={10} /> Code not in current set
              </span>
            )}
          </div>
        </div>

        {/* Row-level reveal affordance */}
        <button
          type="button"
          onClick={onReveal}
          title="Go to field"
          aria-label="Go to field"
          className="shrink-0 p-1 text-gray-400 hover:text-(--brand-primary) hover:bg-white rounded-sm"
        >
          <ExternalLink size={12} />
        </button>
      </div>

      {/* Row 3: primary action per status */}
      <div className="mt-1.5 flex items-center gap-2">
        {isUnlinked && (
          <button
            type="button"
            onClick={onLink}
            className="inline-flex items-center gap-1 text-[11px] font-medium text-white rounded-sm px-2 py-0.5 transition-colors"
            style={{ backgroundColor: 'var(--brand-primary)' }}
          >
            <Link2 size={10} /> Link violation
          </button>
        )}
        {isLinked && (
          <>
            <button
              type="button"
              onClick={onShow}
              className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-700 bg-white hover:bg-gray-100 border border-gray-300 rounded-sm px-2 py-0.5 transition-colors"
            >
              <Eye size={10} /> Show
            </button>
            <button
              type="button"
              onClick={onChange}
              className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-700 bg-white hover:bg-gray-100 border border-gray-300 rounded-sm px-2 py-0.5 transition-colors"
            >
              <Pencil size={10} /> Change
            </button>
            <button
              type="button"
              onClick={onUnlink}
              className="text-[11px] font-medium text-gray-500 hover:text-red-600 underline-offset-2 hover:underline transition-colors"
            >
              Unlink
            </button>
          </>
        )}
        {isStale && (
          <>
            <button
              type="button"
              onClick={onShow}
              className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-700 bg-white hover:bg-gray-100 border border-gray-300 rounded-sm px-2 py-0.5 transition-colors"
            >
              <Eye size={10} /> Show
            </button>
            <button
              type="button"
              onClick={onFix}
              className="inline-flex items-center gap-1 text-[11px] font-medium text-white bg-red-600 hover:bg-red-700 rounded-sm px-2 py-0.5 transition-colors"
            >
              <Wrench size={10} /> Fix
            </button>
          </>
        )}
      </div>
    </li>
  );
}

export default ViolationInventoryCard;
